import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from './tokens';

// =============================================================================
// TYPES
// =============================================================================

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'default';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

// =============================================================================
// CONTEXT
// =============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook to access toast functionality
 *
 * @example
 * const { addToast } = useToast();
 *
 * // Simple toast
 * addToast({ message: 'Changes saved!', variant: 'success' });
 *
 * // With title and action
 * addToast({
 *   title: 'Error',
 *   message: 'Failed to save changes',
 *   variant: 'error',
 *   action: { label: 'Retry', onClick: handleRetry }
 * });
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// =============================================================================
// PROVIDER
// =============================================================================

interface ToastProviderProps {
  children: React.ReactNode;
  /** Maximum number of visible toasts */
  maxToasts?: number;
  /** Default duration in ms */
  defaultDuration?: number;
}

/**
 * Toast provider - wrap your app to enable toast notifications
 *
 * @example
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
  defaultDuration = 5000,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast: Toast = {
        id,
        duration: defaultDuration,
        ...toast,
      };

      setToasts((prev) => {
        const updated = [newToast, ...prev];
        // Limit visible toasts
        return updated.slice(0, maxToasts);
      });

      return id;
    },
    [defaultDuration, maxToasts]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// =============================================================================
// TOAST CONTAINER
// =============================================================================

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[70] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

// =============================================================================
// TOAST ITEM
// =============================================================================

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const variantStyles = {
  default: {
    container: 'bg-slate-800 border-slate-700',
    icon: 'text-slate-400',
    IconComponent: Info,
  },
  success: {
    container: 'bg-slate-800 border-green-500/50',
    icon: 'text-green-400',
    IconComponent: CheckCircle,
  },
  error: {
    container: 'bg-slate-800 border-red-500/50',
    icon: 'text-red-400',
    IconComponent: AlertCircle,
  },
  warning: {
    container: 'bg-slate-800 border-amber-500/50',
    icon: 'text-amber-400',
    IconComponent: AlertTriangle,
  },
  info: {
    container: 'bg-slate-800 border-blue-500/50',
    icon: 'text-blue-400',
    IconComponent: Info,
  },
};

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  // Auto-dismiss timer
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  // Remove after exit animation
  useEffect(() => {
    if (isExiting) {
      const timer = setTimeout(() => {
        onRemove(toast.id);
      }, 200); // Match animation duration

      return () => clearTimeout(timer);
    }
  }, [isExiting, toast.id, onRemove]);

  const handleDismiss = () => {
    setIsExiting(true);
  };

  const { container, icon, IconComponent } = variantStyles[toast.variant];

  return (
    <div
      className={cn(
        // Base styles
        'pointer-events-auto flex items-start gap-3',
        'w-80 max-w-[calc(100vw-32px)]',
        'px-4 py-3 rounded-lg border shadow-lg',
        'transform transition-all duration-200',
        // Animation
        isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0 animate-slideInRight',
        // Variant styles
        container
      )}
      role="alert"
    >
      {/* Icon */}
      <IconComponent size={18} className={cn('flex-shrink-0 mt-0.5', icon)} />

      {/* Content */}
      <div className="flex-grow min-w-0">
        {toast.title && (
          <p className="text-sm font-bold text-slate-50">{toast.title}</p>
        )}
        <p className={cn('text-sm text-slate-300', toast.title && 'mt-0.5')}>
          {toast.message}
        </p>

        {/* Action button */}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-2 text-xs font-medium text-accent hover:text-accent/80 transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-300 rounded transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a simple toast helper for common use cases
 * Use this if you don't need the full useToast hook
 */
export function createToastHelpers(addToast: ToastContextValue['addToast']) {
  return {
    success: (message: string, title?: string) =>
      addToast({ message, title, variant: 'success' }),

    error: (message: string, title?: string) =>
      addToast({ message, title, variant: 'error' }),

    warning: (message: string, title?: string) =>
      addToast({ message, title, variant: 'warning' }),

    info: (message: string, title?: string) =>
      addToast({ message, title, variant: 'info' }),
  };
}

// =============================================================================
// CSS KEYFRAMES (add to your global CSS or tailwind config)
// =============================================================================

/*
Add these keyframes to your tailwind.config.js:

module.exports = {
  theme: {
    extend: {
      keyframes: {
        slideInRight: {
          '0%': { opacity: '0', transform: 'translateX(100%)' },
          '100%': { opacity: '1', transform: 'translateX(0)' },
        },
      },
      animation: {
        slideInRight: 'slideInRight 0.2s ease-out',
      },
    },
  },
}
*/

export default ToastProvider;
